import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { BsX } from "react-icons/bs";
import Config from "../../Config";
import {
  color_black,
  color_green_dark,
  color_grey_dark,
  color_grey_ultra_light,
  color_red_default,
  color_transparent,
  color_white,
} from "../../constants/colors";
import copyLinkIcon from "../../images/CopyLinkIcon.svg";
import copyLinkIconSuccess from "../../images/CopyLinkIconSuccess.svg";
import { useWindowDimensions } from "../../utils";
import { ScheduleInfoHint } from "../blocks/studentProfile/studentSchedule/ScheduleInfoHint";
import { ModalDefault } from "../containers/ModalDefault";
import { Button } from "../controls/Button/Button";
import { OnestNormalSmall, OnestSemiBoldBig } from "../styled/TextComponents";
import { useI18n } from "../../i18n/I18nProvider";

const ClassroomLinksModal = ({ isOpen, onRequestClose }) => {
  const { locale, t } = useI18n();
  const { height: screenHeight, width: screenWidth } = useWindowDimensions();
  const isMobileLandscape = screenWidth > screenHeight && screenHeight <= 500;
  const isNarrowLandscape = isMobileLandscape && screenWidth <= 740;
  const contentWidth = isMobileLandscape
    ? Math.min(560, screenWidth - 16)
    : Math.min(410, screenWidth - 24);
  // Для узкого landscape учитываем внешний inset модалки, чтобы блок не "лип" к низу.
  const contentMaxHeight = isMobileLandscape
    ? isNarrowLandscape
      ? screenHeight - 28
      : screenHeight - 16
    : screenHeight - 48;
  const [hasActive, setHasActive] = useState(false);
  const [teacherUrl, setTeacherUrl] = useState(null);
  const [studentUrl, setStudentUrl] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  // Шаг 4: после копирования показываем «Ссылка скопирована» (teacher | student), сбрасываем через 2 с
  const [copyFeedback, setCopyFeedback] = useState(null);
  const copyFeedbackTimeoutRef = useRef(null);
  // Шаг 5: загрузка при обновлении ссылок (regenerate); при 429 показываем detail в error
  const [isRegenerating, setIsRegenerating] = useState(false);
  const isLocalMockCopyEnabled = true;

  useEffect(() => {
    if (!isOpen) return;
    setIsLoading(true);
    setError(null);
    axios
      .get(Config.BACKEND_ADDR + "/user/classroom/status")
      .then((res) => {
        const data = res.data;
        setHasActive(data.hasActive ?? false);
        setExpiresAt(data.expiresAt ?? null);
        setTeacherUrl(data.links?.teacher ?? null);
        setStudentUrl(data.links?.student ?? null);
      })
      .catch((err) => {
        setError(
          err.response?.data?.detail || err.message || t("Ошибка загрузки"),
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [isOpen, t]);

  useEffect(() => {
    if (isOpen) return;
    setCopyFeedback(null);
    if (copyFeedbackTimeoutRef.current) {
      clearTimeout(copyFeedbackTimeoutRef.current);
      copyFeedbackTimeoutRef.current = null;
    }
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (copyFeedbackTimeoutRef.current) {
        clearTimeout(copyFeedbackTimeoutRef.current);
      }
    };
  }, []);

  // Шаг 7: при открытой модалке и hasActive — polling раз в 30 с при видимой вкладке; при visibilitychange — один запрос
  useEffect(() => {
    if (!isOpen || !hasActive) return;
    const fetchStatusSilent = () => {
      axios
        .get(Config.BACKEND_ADDR + "/user/classroom/status")
        .then((res) => {
          const data = res.data;
          setHasActive(data.hasActive ?? false);
          setExpiresAt(data.expiresAt ?? null);
          setTeacherUrl(data.links?.teacher ?? null);
          setStudentUrl(data.links?.student ?? null);
        })
        .catch(() => {});
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") fetchStatusSilent();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") fetchStatusSilent();
    }, 30000);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      clearInterval(interval);
    };
  }, [isOpen, hasActive]);

  // Шаг 3: POST /user/classroom/generate — создание сессии; ответ: teacherUrl, studentUrl, expiresAt
  const handleGenerate = () => {
    setIsGenerating(true);
    setError(null);
    axios
      .post(Config.BACKEND_ADDR + "/user/classroom/generate", {})
      .then((res) => {
        const data = res.data;
        setTeacherUrl(data.teacherUrl ?? null);
        setStudentUrl(data.studentUrl ?? null);
        setExpiresAt(data.expiresAt ?? null);
        setHasActive(true);
        setError(null);
      })
      .catch((err) => {
        setError(
          err.response?.data?.detail ||
            err.message ||
            t("Ошибка при создании классной комнаты."),
        );
      })
      .finally(() => {
        setIsGenerating(false);
      });
  };

  // Шаг 5: POST /user/classroom/regenerate — обновление ссылок; при 429 (cooldown/лимит) показываем detail
  const handleRegenerate = () => {
    setIsRegenerating(true);
    setError(null);
    axios
      .post(Config.BACKEND_ADDR + "/user/classroom/regenerate", {})
      .then((res) => {
        const data = res.data;
        setTeacherUrl(data.teacherUrl ?? null);
        setStudentUrl(data.studentUrl ?? null);
        setExpiresAt(data.expiresAt ?? null);
        setError(null);
      })
      .catch((err) => {
        const msg =
          err.response?.data?.detail ||
          err.message ||
          t("Ошибка при обновлении ссылок.");
        setError(msg);
      })
      .finally(() => {
        setIsRegenerating(false);
      });
  };

  // Шаг 6: истекла ли сессия по expiresAt (в прошлом)
  const isExpired = expiresAt && new Date(expiresAt).getTime() <= Date.now();

  // Шаг 6: оставшееся время «X ч Y мин»; обновляем раз в минуту
  const [timeLeftLabel, setTimeLeftLabel] = useState("");
  useEffect(() => {
    if (!expiresAt || isExpired) {
      setTimeLeftLabel("");
      return;
    }
    const update = () => {
      const end = new Date(expiresAt).getTime();
      const ms = Math.max(0, end - Date.now());
      if (ms === 0) {
        setTimeLeftLabel("");
        return;
      }
      const hours = Math.floor(ms / 3600000);
      const minutes = Math.floor((ms % 3600000) / 60000);
      setTimeLeftLabel(
        hours > 0
          ? t("{{hours}} ч {{minutes}} мин", { hours, minutes })
          : t("{{minutes}} мин", { minutes }),
      );
    };
    update();
    const intervalId = setInterval(update, 60000);
    return () => clearInterval(intervalId);
  }, [expiresAt, isExpired, t]);

  // Шаг 4: копирование ссылки в буфер (Clipboard API + простой фоллбек)
  const handleCopy = (url, which) => {
    const doFallbackCopy = () => {
      try {
        const textarea = document.createElement("textarea");
        textarea.value = url;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        const ok = document.execCommand("copy"); // Копируем текст в буфер
        document.body.removeChild(textarea);
        return ok;
      } catch (e) {
        console.warn("Fallback copy error", e);
        return false;
      }
    };

    const onCopySuccess = () => {
      setCopyFeedback(which);
      if (copyFeedbackTimeoutRef.current) {
        clearTimeout(copyFeedbackTimeoutRef.current);
      }
      copyFeedbackTimeoutRef.current = setTimeout(() => {
        setCopyFeedback(null);
        copyFeedbackTimeoutRef.current = null;
      }, 1000);
    };

    if (!url) {
      if (isLocalMockCopyEnabled) {
        onCopySuccess();
      }
      return;
    }

    if (
      navigator.clipboard &&
      typeof navigator.clipboard.writeText === "function"
    ) {
      navigator.clipboard
        .writeText(url)
        .then(() => {
          onCopySuccess();
        })
        .catch((err) => {
          console.warn("Clipboard API error, fallback to execCommand", err);
          const copied = doFallbackCopy();
          if (copied) onCopySuccess();
        });
    } else {
      const copied = doFallbackCopy();
      if (copied) onCopySuccess();
    }
  };

  const linkRows = [
    { label: t("Ссылка для учителя"), url: teacherUrl, key: "teacher" },
    { label: t("Ссылка для ученика"), url: studentUrl, key: "student" },
  ];
  const boxStyle = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "nowrap",
    backgroundColor: color_grey_ultra_light,
    borderRadius: 8,
    padding: isMobileLandscape ? "8px 10px" : "10px 12px",
  };
  const copyBtnStyle = {
    flexShrink: 0,
    padding: 6,
    background: "none",
    border: "none",
    color: color_grey_dark,
  };
  const secondaryBtnStyle = {
    alignSelf: "flex-start",
    padding: isMobileLandscape ? "6px 14px" : "8px 16px",
    fontSize: isMobileLandscape ? 13 : 14,
    borderRadius: 8,
    backgroundColor: "#F5F5F5",
    border: "none",
    cursor: "pointer",
  };

  return (
    <ModalDefault
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      modalWidth={contentWidth}
      centerAlways={isNarrowLandscape}
    >
      <div
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          backgroundColor: color_white,
          borderRadius: 15,
          padding: isMobileLandscape ? 16 : 25,
          width: contentWidth,
          maxWidth: "100%",
          maxHeight: contentMaxHeight,
          overflowY: "auto",
          gap: isMobileLandscape ? 18 : 32,
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: isMobileLandscape ? 10 : 20,
            right: isMobileLandscape ? 10 : 20,
            cursor: "pointer",
            zIndex: 1000,
          }}
          onClick={onRequestClose}
        >
          <BsX
            style={{
              fontSize: isMobileLandscape ? 24 : 28,
              color: color_grey_dark,
            }}
          />
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: isMobileLandscape ? 8 : 10,
          }}
        >
          <OnestSemiBoldBig
            style={{
              fontSize: isMobileLandscape ? 20 : 24,
              lineHeight: "110%",
              letterSpacing: "-0.03em",
              paddingRight: 28,
            }}
          >
            {t("Сформируйте ссылки доступа к сессии")}
          </OnestSemiBoldBig>
          {isLoading && <div style={{ padding: "8px 0" }}>{t("Загрузка…")}</div>}
          {!isLoading && (
            <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
              <OnestNormalSmall
                style={{
                  fontSize: isMobileLandscape ? 14 : 16,
                  lineHeight: "120%",
                  letterSpacing: "-0.03em",
                  color: color_black,
                  margin: 0,
                  flex: 1,
                }}
              >
                {t("Для корректной работы учителю и ученику рекомендуется использовать компьютер и браузер Google Chrome.")}
              </OnestNormalSmall>
              <ScheduleInfoHint
                title={t("Запись урока")}
                align="left"
                steps={[t("На Windows используйте Win + Alt + R (старт/стоп)")]}
              />
            </div>
          )}
        </div>

        {!isLoading && (
          <>
            {error && (
              <div style={{ color: "#c00", fontSize: 14 }}>{error}</div>
            )}

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: isMobileLandscape ? 10 : 16,
              }}
            >
              {linkRows.map(({ label, url, key }) => (
                <div key={key}>
                  <div
                    style={{
                      fontSize: isMobileLandscape ? 13 : 14,
                      color: color_black,
                      marginBottom: 6,
                      fontFamily: "Onest",
                    }}
                  >
                    {label}
                  </div>
                  <div style={boxStyle}>
                    <span
                      style={{
                        flex: 1,
                        minWidth: 0,
                        fontSize: isMobileLandscape ? 13 : 14,
                        whiteSpace: "nowrap",
                        overflow: "hidden", // Скрываем текст CCЫЛКИ, если он не помещается
                        textOverflow: "ellipsis",
                        color: url ? "inherit" : "#999",
                      }}
                    >
                      {url ? (
                        <a
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: "inherit", textDecoration: "none" }}
                        >
                          {url}
                        </a>
                      ) : (
                        t("Ссылка")
                      )}
                    </span>
                    {copyFeedback === key && (
                      <span
                        style={{
                          fontSize: 12,
                          color: color_green_dark,
                          flexShrink: 0,
                        }}
                      >
                        {t("Скопировано")}
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => handleCopy(url, key)}
                      disabled={!url && !isLocalMockCopyEnabled}
                      style={{
                        ...copyBtnStyle,
                        padding: isMobileLandscape ? 4 : copyBtnStyle.padding,
                        cursor:
                          url || isLocalMockCopyEnabled ? "pointer" : "default",
                      }}
                      aria-label={t("Копировать")}
                    >
                      <img
                        src={
                          copyFeedback === key
                            ? copyLinkIconSuccess
                            : copyLinkIcon
                        }
                        alt=""
                        width={isMobileLandscape ? 14 : 16}
                        height={isMobileLandscape ? 14 : 16}
                        style={{ display: "block" }}
                      />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {timeLeftLabel && (
              <div style={{ fontSize: 12, color: "#666" }}>
                {t("Истекает через: {{timeLeft}}", { timeLeft: timeLeftLabel })}
              </div>
            )}
            {expiresAt && !timeLeftLabel && (
              <div style={{ fontSize: 12, color: "#666" }}>
                {t("Истекает: {{date}}", { date: new Date(expiresAt).toLocaleString(locale === "en" ? "en-GB" : "ru-RU") })}
              </div>
            )}

            {hasActive && !isExpired && (
              <button
                type="button"
                onClick={handleRegenerate}
                disabled={isRegenerating}
                style={secondaryBtnStyle}
              >
                {isRegenerating ? t("Обновляю…") : t("Обновить ссылки")}
              </button>
            )}
            {hasActive && isExpired && (
              <button
                type="button"
                onClick={handleGenerate}
                disabled={isGenerating}
                style={secondaryBtnStyle}
              >
                {isGenerating ? t("Создаём…") : t("Создать новую классную комнату")}
              </button>
            )}

            <Button
              outline={false}
              type="button"
              width="100%"
              height={isMobileLandscape ? 36 : 40}
              disabled={hasActive ? false : isGenerating}
              backgroundColor={color_red_default}
              color={color_white}
              borderColor={color_transparent}
              fontSize={isMobileLandscape ? 15 : 16}
              onClick={hasActive ? onRequestClose : handleGenerate}
              style={{ marginTop: 8, fontWeight: 500 }}
            >
              {hasActive
                ? t("Закрыть")
                : isGenerating
                  ? t("Создаём…")
                  : t("Создать ссылки")}
            </Button>
          </>
        )}
      </div>
    </ModalDefault>
  );
};

export default ClassroomLinksModal;
